/** Trades Poster — Form Inventory — register options, helpers, and starting catalogue */

export const FORM_INVENTORY_ROUTE = '/admin/form-inventory';

export const LIST_PREVIEW_COUNT = 2;

export const USED_BY_OPTIONS = [
  'Visitor',
  'Customer',
  'Business',
  'Platform Owner',
  'Co-owner',
  'Team Member',
];

export const FORM_CATEGORY_OPTIONS = [
  'Account Access',
  'Customer Accounts',
  'Business Accounts',
  'Account Verification',
  'Passwords & Security',
  'Data & Privacy',
  'Plans & Subscriptions',
  'Trade Search',
  'Posts',
  'Quotes',
  'Messages',
  'Reviews',
  'Flagging',
  'Support Tickets',
  'Appeals',
  'Compliance',
  'Moderation',
  'Fraud & Security',
  'Case Actions',
  'Team Members',
  'Reports',
];

export const OWNING_MODULE_OPTIONS = [
  'Account Access',
  'Admin Access',
  'Customers',
  'Businesses',
  'Settings',
  'Billing & Subscriptions',
  'Trade Search',
  'Posts',
  'Quotes',
  'Messages',
  'Reviews',
  'Flagging',
  'Support Tickets',
  'Appeals',
  'Compliance',
  'Moderation',
  'Fraud & Security',
  'Case Actions',
  'Team Members',
  'Reports',
];

export const CONTROL_TYPE_OPTIONS = [
  'Text input',
  'Textarea',
  'Email input',
  'Telephone input',
  'Number input',
  'Dropdown',
  'Searchable dropdown',
  'Multi-select dropdown',
  'Radio buttons',
  'Checkbox',
  'Password input',
  'Date picker',
  'File upload',
  'Read-only display',
];

export const SELECTABLE_VALUE_SOURCE_OPTIONS = ['Fixed Form Values', 'Saved Source Records'];

export const SELECTABLE_CONTROL_TYPES = [
  'Dropdown',
  'Searchable dropdown',
  'Multi-select dropdown',
  'Radio buttons',
];

export const DATA_SOURCE_OPTIONS = ['User Input', 'System Value', 'Saved Source Relationship'];

export const SAVED_SOURCE_RELATIONSHIP_OPTIONS = [
  'Trades Poster — Trade Categories → saved Trade Category records',
  'Trades Poster — Keywords → saved Keyword records',
  'Trades Poster — Plans → saved Plan records',
  'Trades Poster — Team Members → saved Team Member records',
  'Trades Poster — Support Ticket Categories → saved Category records',
];

export const RECORD_DESTINATION_OPTIONS = [
  'Posts → Trade Category ID',
  'Posts → Post Title',
  'Posts → Post Description',
  'Posts → Location',
  'Account Access → Email Address',
  'Account Access → Password',
  'Businesses → Business Name',
  'Customers → Customer Name',
  'Support Tickets → Resolution Summary',
  'Moderation → Decision Outcome',
  'Team Members → Member Profile',
  'Reports → Report Parameters',
  'Trade Search → Request Input',
];

export const MESSAGES = {
  EMPTY_TABLE: 'No Forms have been registered yet.',
  saved: 'Form saved successfully.',
  updated: 'Form updated successfully.',
};

export const VALIDATION_MESSAGES = {
  formKeyMissing: 'Enter the Form Key.',
  duplicateFormKey: 'A Form with this Form Key already exists.',
  formNameMissing: 'Enter the Form Name.',
  duplicateFormName: 'A Form with this name already exists.',
  purposeMissing: 'Enter the Form Purpose.',
  formCategoryMissing: 'Select the Form Category.',
  owningModuleMissing: 'Select the Owning Module.',
  usedByMissing: 'Select at least one Used By value.',
  entryPointMissing: 'Add at least one Entry Point.',
  duplicateEntryPoints: 'Duplicate Entry Points within the same Form are blocked.',
  modesMissing: 'Add at least one Mode.',
  duplicateModes: 'Duplicate Modes within the same Form are blocked.',
  noFormFields: 'Add at least one field definition.',
  fieldLabelMissing: 'Enter the Field Label.',
  fieldKeyMissing: 'Enter the Field Key.',
  duplicateFieldKey: 'This Field Key is already used by another field in this Form.',
  controlTypeMissing: 'Select the Control Type.',
  displayOrderInvalid: 'Enter a unique Display Order for the applicable Mode.',
  availableModesMissing: 'Select at least one Available Mode.',
  unsupportedMode: 'The field can use only Modes saved against the parent Form.',
  selectableValueSourceMissing: 'Select the source of the selectable values.',
  bothValueSources: 'Use Fixed Form Values or Saved Source Records, not both.',
  fixedFormValuesMissing: 'Add at least one Fixed Form Value.',
  savedSourceMissing: 'Select the source register and record set.',
  choiceSourceOnNonSelection: 'Remove choice-source settings from a non-selection control.',
  dataSourceMissing: 'Select the Data Source.',
  destinationMissing: 'Select the Destination.',
  sampleValueMissing: 'Enter a realistic example value.',
  permanentIdChange: 'Permanent IDs and saved keys cannot be replaced.',
};

const padId = (n, width = 6) => String(n).padStart(width, '0');

export const formatFormId = (n) => `FORM-${padId(n)}`;
export const formatFieldId = (n) => `FORM-FIELD-${padId(n)}`;

/** @deprecated Prefer formatFieldId — kept for older FIELD- IDs during transition. */
export const formatLegacyFieldId = (n) => `FIELD-${padId(n)}`;

export const formatFormInventoryDate = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
};

export const formatFormInventoryDateTime = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  const date = d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  const time = d.toLocaleTimeString('en-AU', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
    timeZone: 'Australia/Sydney',
  });
  return `${date}, ${time} AEST`;
};

export const getFieldCount = (form) => (form?.fields || []).length;

export const getFormKey = (form) =>
  String(form?.formKey || form?.implementedFormKey || '').trim();

export const getFieldKey = (field) =>
  String(field?.fieldKey || field?.internalFieldKey || '').trim();

export const createEmptyFormField = (overrides = {}) => ({
  id: '',
  fieldLabel: '',
  fieldKey: '',
  controlType: '',
  displayOrder: 1,
  availableModes: [],
  selectableValueSource: '',
  fixedFormValues: [''],
  savedSourceRelationship: '',
  placeholder: '',
  sampleValue: '',
  dataSource: '',
  destination: '',
  ...overrides,
});

export const createEmptyForm = (overrides = {}) => ({
  id: '',
  formKey: '',
  formName: '',
  purpose: '',
  category: '',
  owningModule: '',
  usedBy: [],
  entryPoints: [''],
  modes: [''],
  dateAdded: '',
  addedBy: '',
  lastUpdated: '',
  lastUpdatedBy: '',
  fields: [],
  ...overrides,
});

const slugifyKey = (value) =>
  String(value || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_|_$/g, '');

const SAMPLE_STAMP = '2026-08-03T00:15:00.000Z';
const SAMPLE_STAMP_UPDATED = '2026-08-04T04:30:00.000Z';
const SAMPLE_ACTOR = 'Development Team Member — DEV-000014';

const makeField = (fieldNum, label, modes, order, extras = {}) => {
  const fieldKey = extras.fieldKey || slugifyKey(label) || `field_${fieldNum}`;
  const isSelectable = SELECTABLE_CONTROL_TYPES.includes(extras.controlType || 'Text input');
  const selectableValueSource =
    extras.selectableValueSource ||
    (isSelectable ? (extras.savedSourceRelationship ? 'Saved Source Records' : 'Fixed Form Values') : '');
  return createEmptyFormField({
    id: formatFieldId(fieldNum),
    fieldLabel: label,
    fieldKey,
    controlType: extras.controlType || 'Text input',
    displayOrder: order,
    availableModes: [...modes],
    selectableValueSource,
    fixedFormValues:
      selectableValueSource === 'Fixed Form Values'
        ? extras.fixedFormValues || ['Option A', 'Option B']
        : [],
    savedSourceRelationship:
      selectableValueSource === 'Saved Source Records' ? extras.savedSourceRelationship || '' : '',
    placeholder: extras.placeholder || '',
    sampleValue: extras.sampleValue || 'Sample',
    dataSource: extras.dataSource || 'User Input',
    destination: extras.destination || extras.recordDestination || 'Operational Module → Field',
  });
};

const makeSimpleFields = (startNum, labels, modes) =>
  labels.map((label, index) => makeField(startNum + index, label, modes, index + 1));

/** Post Form — Trade Category sample from the register specification. */
const buildPostFormFields = () => {
  const modes = ['Create', 'Edit'];
  return [
    makeField(200, 'Post Title', modes, 1, {
      fieldKey: 'post_title',
      placeholder: 'Enter post title',
      sampleValue: 'Emergency plumbing repair',
      destination: 'Posts → Post Title',
    }),
    makeField(201, 'Trade Category', modes, 2, {
      fieldKey: 'trade_category_id',
      controlType: 'Searchable dropdown',
      selectableValueSource: 'Saved Source Records',
      savedSourceRelationship:
        'Trades Poster — Trade Categories → saved Trade Category records',
      placeholder: 'Select trade category',
      sampleValue: 'Plumbing',
      dataSource: 'User Input',
      destination: 'Posts → Trade Category ID',
    }),
    makeField(202, 'Post Description', modes, 3, {
      fieldKey: 'post_description',
      controlType: 'Textarea',
      placeholder: 'Describe the post',
      sampleValue: 'Available same-day for blocked drains.',
      destination: 'Posts → Post Description',
    }),
    makeField(203, 'Location', modes, 4, {
      fieldKey: 'location',
      placeholder: 'Enter location',
      sampleValue: 'Sydney NSW',
      destination: 'Posts → Location',
    }),
    makeField(204, 'Uploaded Image', modes, 5, {
      fieldKey: 'uploaded_image',
      controlType: 'File upload',
      placeholder: 'Upload image',
      sampleValue: 'post-image.jpg',
      destination: 'Posts → Uploaded Image',
    }),
    makeField(205, 'Budget Range', modes, 6, {
      fieldKey: 'budget_range',
      controlType: 'Dropdown',
      fixedFormValues: ['Under $500', '$500–$1000', 'Over $1000'],
      placeholder: 'Select budget range',
      sampleValue: '$500–$1000',
      destination: 'Posts → Budget Range',
    }),
    makeField(206, 'Preferred Contact Method', modes, 7, {
      fieldKey: 'preferred_contact_method',
      controlType: 'Radio buttons',
      fixedFormValues: ['Phone', 'Message', 'Either'],
      sampleValue: 'Message',
      destination: 'Posts → Preferred Contact Method',
    }),
    makeField(207, 'Accept Quote Requests', modes, 8, {
      fieldKey: 'accept_quote_requests',
      controlType: 'Checkbox',
      sampleValue: 'Yes',
      destination: 'Posts → Accept Quote Requests',
    }),
    makeField(208, 'Visibility', modes, 9, {
      fieldKey: 'visibility',
      controlType: 'Dropdown',
      fixedFormValues: ['Public', 'Private'],
      sampleValue: 'Public',
      destination: 'Posts → Visibility',
    }),
    makeField(209, 'Keyword Selection', modes, 10, {
      fieldKey: 'keyword_ids',
      controlType: 'Multi-select dropdown',
      selectableValueSource: 'Saved Source Records',
      savedSourceRelationship: 'Trades Poster — Keywords → saved Keyword records',
      placeholder: 'Select keywords',
      sampleValue: 'Blocked drain',
      destination: 'Posts → Keyword IDs',
    }),
    makeField(210, 'Subcategory', modes, 11, {
      fieldKey: 'subcategory_id',
      controlType: 'Searchable dropdown',
      selectableValueSource: 'Saved Source Records',
      savedSourceRelationship:
        'Trades Poster — Trade Categories → saved Trade Category records',
      placeholder: 'Select subcategory',
      sampleValue: 'Blocked drains',
      destination: 'Posts → Subcategory ID',
    }),
  ];
};

const buildDecisionFields = (startNum, labels, optionalLabels = []) =>
  labels.map((label, index) =>
    makeField(startNum + index, label, ['Decision'], index + 1, {
      controlType: 'Checkbox',
      sampleValue: optionalLabels.includes(label) ? 'Optional check' : 'Checked',
      destination: 'Case → Decision Checklist',
    }),
  );

/**
 * Starting catalogue — 55 skeletons.
 * Fixed IDs retain source identities. On Save entries receive demo IDs from 049+.
 * Historical FORM-000007/008/009/027/036/037/038 are preserved unused.
 */
export const STARTING_FORM_CATALOGUE = [
  { id: 'FORM-000001', formKey: 'customer_business_login_form', formName: 'Customer & Business Login Form', category: 'Account Access', owningModule: 'Account Access', usedBy: ['Customer', 'Business'], entryPoints: ['Customer Login Page', 'Business Login Page'], modes: ['Login'], purpose: 'Allows a Customer or Business to sign in.', fieldLabels: ['Email Address', 'Password', 'Remember Me'] },
  { id: 'FORM-000002', formKey: 'admin_team_login_form', formName: 'Admin & Team Login Form', category: 'Account Access', owningModule: 'Admin Access', usedBy: ['Platform Owner', 'Co-owner', 'Team Member'], entryPoints: ['Admin Login Page'], modes: ['Login'], purpose: 'Allows Admin and Team Member sign-in.', fieldLabels: ['Email Address', 'Password'] },
  { id: 'FORM-000003', formKey: 'customer_signup_form', formName: 'Customer Signup Form', category: 'Customer Accounts', owningModule: 'Customers', usedBy: ['Visitor'], entryPoints: ['Public Header', 'Customer Signup Page'], modes: ['Signup'], purpose: 'Creates a Customer account.', fieldLabels: ['Full Name', 'Email Address', 'Password', 'Mobile Number'] },
  { id: 'FORM-000004', formKey: 'business_signup_form', formName: 'Business Signup Form', category: 'Business Accounts', owningModule: 'Businesses', usedBy: ['Visitor'], entryPoints: ['Public Header', 'Business Signup Page'], modes: ['Signup'], purpose: 'Creates a Business account.', fieldLabels: ['Business Name', 'Email Address', 'Password', 'Mobile Number', 'ABN'] },
  { id: 'FORM-000005', formKey: 'customer_profile_form', formName: 'Customer Profile Form', category: 'Customer Accounts', owningModule: 'Customers', usedBy: ['Customer'], entryPoints: ['Customer Dashboard — Profile'], modes: ['View', 'Edit'], purpose: 'Displays and updates a Customer profile.', fieldLabels: ['Full Name', 'Email Address', 'Mobile Number'] },
  { id: 'FORM-000006', formKey: 'business_profile_form', formName: 'Business Profile Form', category: 'Business Accounts', owningModule: 'Businesses', usedBy: ['Business'], entryPoints: ['Business Dashboard — Profile'], modes: ['View', 'Edit'], purpose: 'Displays and updates a Business profile.', fieldLabels: ['Business Name', 'Email Address', 'Mobile Number', 'About'] },
  { id: 'FORM-000010', formKey: 'customer_notification_preferences_form', formName: 'Customer Notification Preferences Form', category: 'Customer Accounts', owningModule: 'Customers', usedBy: ['Customer'], entryPoints: ['Customer Dashboard — Notifications'], modes: ['Manage Preferences'], purpose: 'Updates Customer notification preferences.', fieldLabels: ['Email Notifications', 'SMS Notifications'] },
  { id: 'FORM-000011', formKey: 'business_notification_preferences_form', formName: 'Business Notification Preferences Form', category: 'Business Accounts', owningModule: 'Businesses', usedBy: ['Business'], entryPoints: ['Business Dashboard — Notifications'], modes: ['Manage Preferences'], purpose: 'Updates Business notification preferences.', fieldLabels: ['Email Notifications', 'SMS Notifications'] },
  { id: 'FORM-000012', formKey: 'customer_close_account_form', formName: 'Customer Close Account Form', category: 'Customer Accounts', owningModule: 'Customers', usedBy: ['Customer'], entryPoints: ['Customer Dashboard — Close Account'], modes: ['Close Account'], purpose: 'Submits a Customer close-account request.', fieldLabels: ['Reason', 'Confirmation'] },
  { id: 'FORM-000013', formKey: 'business_close_account_form', formName: 'Business Close Account Form', category: 'Business Accounts', owningModule: 'Businesses', usedBy: ['Business'], entryPoints: ['Business Dashboard — Close Account'], modes: ['Close Account'], purpose: 'Submits a Business close-account request.', fieldLabels: ['Reason', 'Confirmation'] },
  { id: 'FORM-000014', formKey: 'account_export_request_form', formName: 'Account Export Request Form', category: 'Data & Privacy', owningModule: 'Settings', usedBy: ['Customer', 'Business'], entryPoints: ['Account Settings — Data Export'], modes: ['Create Request'], purpose: 'Requests an account data export.', fieldLabels: ['Request Reason'] },
  { id: 'FORM-000015', formKey: 'account_anonymisation_request_form', formName: 'Account Anonymisation Request Form', category: 'Data & Privacy', owningModule: 'Settings', usedBy: ['Customer', 'Business'], entryPoints: ['Account Settings — Anonymisation'], modes: ['Create Request'], purpose: 'Requests account anonymisation.', fieldLabels: ['Request Reason'] },
  { id: 'FORM-000016', formKey: 'restore_request_form', formName: 'Restore Request Form', category: 'Data & Privacy', owningModule: 'Settings', usedBy: ['Customer', 'Business'], entryPoints: ['Account Settings — Restore'], modes: ['Create Request'], purpose: 'Requests restoration of an anonymised account.', fieldLabels: ['Request Reason'] },
  { id: 'FORM-000017', formKey: 'business_portfolio_form', formName: 'Business Portfolio Form', category: 'Business Accounts', owningModule: 'Businesses', usedBy: ['Business'], entryPoints: ['Business Dashboard — Portfolio'], modes: ['Add', 'Edit'], purpose: 'Adds or edits Business portfolio items.', fieldLabels: ['Title', 'Description', 'Image'] },
  { id: 'FORM-000018', formKey: 'plan_subscription_form', formName: 'Plan & Subscription Form', category: 'Plans & Subscriptions', owningModule: 'Billing & Subscriptions', usedBy: ['Business'], entryPoints: ['Business Dashboard — Subscription'], modes: ['Select', 'Reactivate'], purpose: 'Selects a plan when starting or reactivating a subscription.', fieldLabels: ['Plan', 'Billing Cycle'] },
  { id: 'FORM-000019', formKey: 'trade_search_form', formName: 'Trade Search Form', category: 'Trade Search', owningModule: 'Trade Search', usedBy: ['Visitor', 'Customer'], entryPoints: ['Public Trade Search'], modes: ['Search'], purpose: 'Submits a Trade Search request.', fieldLabels: ['Trade Category', 'Location', 'Keywords'] },
  { id: 'FORM-000020', formKey: 'post_form', formName: 'Post Form', category: 'Posts', owningModule: 'Posts', usedBy: ['Business'], entryPoints: ['Business Dashboard — Posts', 'Post Drafts'], modes: ['Create', 'Edit'], purpose: 'Allows a Business to create or edit a Post.', fieldsBuilder: buildPostFormFields },
  { id: 'FORM-000021', formKey: 'quote_request_form', formName: 'Quote Request Form', category: 'Quotes', owningModule: 'Quotes', usedBy: ['Customer'], entryPoints: ['Post Detail — Request Quote', 'Clarify Quote'], modes: ['Quote Request', 'Clarify'], purpose: 'Submits or clarifies a quote request.', fieldLabels: ['Message', 'Preferred Timing'] },
  { id: 'FORM-000022', formKey: 'message_form', formName: 'Message Form', category: 'Messages', owningModule: 'Messages', usedBy: ['Customer', 'Business'], entryPoints: ['Messages — Conversation'], modes: ['Start Conversation', 'Reply'], purpose: 'Sends the first message or a reply.', fieldLabels: ['Message Body', 'Attachment'] },
  { id: 'FORM-000023', formKey: 'quote_reply_form', formName: 'Quote Reply Form', category: 'Quotes', owningModule: 'Quotes', usedBy: ['Business'], entryPoints: ['Quotes — Reply'], modes: ['Reply'], purpose: 'Sends a Business quote reply.', fieldLabels: ['Quote Amount', 'Message'] },
  { id: 'FORM-000024', formKey: 'review_form', formName: 'Review Form', category: 'Reviews', owningModule: 'Reviews', usedBy: ['Customer'], entryPoints: ['Reviews — Create', 'Reviews — Edit'], modes: ['Create', 'Edit'], purpose: 'Creates or edits a Customer review.', fieldLabels: ['Rating', 'Review Text'] },
  { id: 'FORM-000025', formKey: 'review_reply_form', formName: 'Review Reply Form', category: 'Reviews', owningModule: 'Reviews', usedBy: ['Business'], entryPoints: ['Reviews — Reply'], modes: ['Reply'], purpose: 'Sends a Business reply to a review.', fieldLabels: ['Reply Text'] },
  { id: 'FORM-000026', formKey: 'post_flag_form', formName: 'Post Flag Form', category: 'Flagging', owningModule: 'Flagging', usedBy: ['Customer', 'Business'], entryPoints: ['Post Detail — Flag'], modes: ['Flag'], purpose: 'Flags a Post.', fieldLabels: ['Reason', 'Details'] },
  { id: 'FORM-000028', formKey: 'message_flag_form', formName: 'Message Flag Form', category: 'Flagging', owningModule: 'Flagging', usedBy: ['Customer', 'Business'], entryPoints: ['Messages — Flag'], modes: ['Flag'], purpose: 'Flags a Message and may include participant block.', fieldLabels: ['Reason', 'Details', 'Block Participant'] },
  { id: 'FORM-000029', formKey: 'quote_flag_form', formName: 'Quote Flag Form', category: 'Flagging', owningModule: 'Flagging', usedBy: ['Customer', 'Business'], entryPoints: ['Quotes — Flag'], modes: ['Flag'], purpose: 'Flags a Quote and may include participant block.', fieldLabels: ['Reason', 'Details', 'Block Participant'] },
  { id: 'FORM-000030', formKey: 'support_ticket_submission_form', formName: 'Support Ticket Submission Form', category: 'Support Tickets', owningModule: 'Support Tickets', usedBy: ['Visitor', 'Customer', 'Business'], entryPoints: ['Help Centre', 'Contact Us', 'Customer Dashboard', 'Business Dashboard'], modes: ['Submit'], purpose: 'Submits a support ticket from Help Centre, Contact Us or an authenticated dashboard.', fieldLabels: ['Submitted As', 'Submitted By', 'Email', 'Ticket Type', 'Subject', 'Message', 'Attachments'] },
  { id: 'FORM-000031', formKey: 'support_ticket_reply_form', formName: 'Support Ticket Reply Form', category: 'Support Tickets', owningModule: 'Support Tickets', usedBy: ['Visitor', 'Customer', 'Business', 'Team Member'], entryPoints: ['Support Ticket Screen — Reply'], modes: ['Reply'], purpose: 'Replies on a support ticket.', fieldLabels: ['Message', 'Attachments'] },
  { id: 'FORM-000032', formKey: 'support_ticket_resolution_form', formName: 'Support Ticket Resolution Form', category: 'Support Tickets', owningModule: 'Support Tickets', usedBy: ['Team Member'], entryPoints: ['Support Ticket Screen — Support Messages'], modes: ['Resolve'], purpose: 'Records a Support Ticket Resolution Summary and marks the ticket Resolved.', fieldLabels: ['Resolution Summary'] },
  { id: 'FORM-000033', formKey: 'appeal_submission_form', formName: 'Appeal Submission Form', category: 'Appeals', owningModule: 'Appeals', usedBy: ['Customer', 'Business'], entryPoints: ['Appeals — Submit'], modes: ['Submit'], purpose: 'Submits an appeal.', fieldLabels: ['Appeal Reason', 'Supporting Details'] },
  { id: 'FORM-000034', formKey: 'appeal_reply_form', formName: 'Appeal Reply Form', category: 'Appeals', owningModule: 'Appeals', usedBy: ['Customer', 'Business', 'Team Member'], entryPoints: ['Appeals — Reply'], modes: ['Reply'], purpose: 'Replies on an appeal.', fieldLabels: ['Reply Message'] },
  { id: 'FORM-000035', formKey: 'appeal_decision_form', formName: 'Appeal Decision Form', category: 'Appeals', owningModule: 'Appeals', usedBy: ['Team Member'], entryPoints: ['Appeals — Decision'], modes: ['Create Decision', 'Correct Decision'], purpose: 'Records an appeal decision, including Correction Mode.', fieldLabels: ['Decision Outcome', 'Decision Reason'] },
  {
    id: 'FORM-000039',
    formKey: 'compliance_decision_form',
    formName: 'Compliance Decision Form',
    category: 'Compliance',
    owningModule: 'Compliance',
    usedBy: ['Team Member'],
    entryPoints: ['Compliance Case Screen'],
    modes: ['Decision'],
    purpose: 'Records Compliance decision inputs, checklist, Reject content and Ask More Info content.',
    fieldsBuilder: () =>
      buildDecisionFields(390, [
        'Evidence readable',
        'Submitted details match evidence',
        'Holder details confirmed',
        'Number confirmed',
        'Issuer confirmed',
        'Jurisdiction confirmed',
        'Class and endorsements confirmed',
        'Dates confirmed',
        'State register checked',
        'Decision requirements completed',
      ], ['State register checked']),
  },
  {
    id: 'FORM-000040',
    formKey: 'moderation_decision_form',
    formName: 'Moderation Decision Form',
    category: 'Moderation',
    owningModule: 'Moderation',
    usedBy: ['Team Member'],
    entryPoints: ['Moderation Case Screen'],
    modes: ['Decision'],
    purpose:
      'Records Moderation checklist and Decision Reason. Outcomes are Approve, Not Approve, Request More Information, or Transfer via Case Actions.',
    fieldsBuilder: () =>
      buildDecisionFields(400, [
        'Captured evidence reviewed',
        'Flagged items identified',
        'Decision Reason completed',
        'Decision Form requirements completed',
      ]),
  },
  { id: 'FORM-000041', formKey: 'fraud_security_information_request_form', formName: 'Fraud & Security Information Request Form', category: 'Fraud & Security', owningModule: 'Fraud & Security', usedBy: ['Team Member'], entryPoints: ['Fraud Case — Information Request'], modes: ['Information Request'], purpose: 'Requests information on a Fraud & Security case.', fieldLabels: ['Request Summary', 'Required Documents'] },
  { id: 'FORM-000042', formKey: 'fraud_security_information_response_form', formName: 'Fraud & Security Information Response Form', category: 'Fraud & Security', owningModule: 'Fraud & Security', usedBy: ['Customer', 'Business'], entryPoints: ['Fraud Case — Information Response'], modes: ['Information Response'], purpose: 'Responds to a Fraud & Security information request.', fieldLabels: ['Response Message', 'Attachment'] },
  { id: 'FORM-000043', formKey: 'fraud_security_decision_form', formName: 'Fraud & Security Decision Form', category: 'Fraud & Security', owningModule: 'Fraud & Security', usedBy: ['Team Member'], entryPoints: ['Fraud Case — Decision'], modes: ['Decision'], purpose: 'Records a Fraud & Security decision.', fieldLabels: ['Decision Outcome', 'Decision Reason'] },
  { id: 'FORM-000044', formKey: 'case_action_form', formName: 'Case Action Form', category: 'Case Actions', owningModule: 'Case Actions', usedBy: ['Team Member'], entryPoints: ['Case Screen — Actions'], modes: ['Perform Action'], purpose: 'Performs a supported case action.', fieldLabels: ['Action Type', 'Action Notes'] },
  { id: 'FORM-000045', formKey: 'team_member_form', formName: 'Team Member Form', category: 'Team Members', owningModule: 'Team Members', usedBy: ['Platform Owner'], entryPoints: ['Admin Dashboard — Team Members'], modes: ['Add', 'View', 'Edit'], purpose: 'Adds, views or edits a Team Member skeleton.', fieldLabels: ['Full Name', 'Email Address', 'Role', 'Mobile Number'] },
  { id: 'FORM-000046', formKey: 'team_password_reset_form', formName: 'Team Password Reset Form', category: 'Passwords & Security', owningModule: 'Team Members', usedBy: ['Platform Owner', 'Team Member'], entryPoints: ['Admin — Team Password Reset'], modes: ['Password Reset'], purpose: 'Resets a Team Member password.', fieldLabels: ['New Password', 'Confirm Password'] },
  { id: 'FORM-000047', formKey: 'team_password_setup_form', formName: 'Team Password Setup Form', category: 'Passwords & Security', owningModule: 'Team Members', usedBy: ['Team Member'], entryPoints: ['Team Invite — Password Setup'], modes: ['Password Setup'], purpose: 'Sets the initial Team Member password.', fieldLabels: ['New Password', 'Confirm Password'] },
  { id: 'FORM-000048', formKey: 'generate_report_form', formName: 'Generate Report Form', category: 'Reports', owningModule: 'Reports', usedBy: ['Platform Owner', 'Co-owner', 'Team Member'], entryPoints: ['Admin Dashboard — Reports'], modes: ['Generate Report'], purpose: 'Generates an admin report.', fieldLabels: ['Report Type', 'Date From', 'Date To', 'Format'] },
  // On Save identities — demo seed IDs from 049+
  { id: 'FORM-000049', formKey: 'customer_mobile_verification_form', formName: 'Customer Mobile Verification Form', category: 'Account Verification', owningModule: 'Customers', usedBy: ['Customer'], entryPoints: ['Customer Signup — Mobile Verification', 'Customer Profile — Mobile Change'], modes: ['Verify', 'Re-verify'], purpose: 'Verifies a Customer mobile number.', fieldLabels: ['Mobile Number', 'Verification Code'] },
  { id: 'FORM-000050', formKey: 'business_mobile_verification_form', formName: 'Business Mobile Verification Form', category: 'Account Verification', owningModule: 'Businesses', usedBy: ['Business'], entryPoints: ['Business Signup — Mobile Verification', 'Business Profile — Mobile Change'], modes: ['Verify', 'Re-verify'], purpose: 'Verifies a Business mobile number.', fieldLabels: ['Mobile Number', 'Verification Code'] },
  { id: 'FORM-000051', formKey: 'team_member_mobile_verification_form', formName: 'Team Member Mobile Verification Form', category: 'Account Verification', owningModule: 'Team Members', usedBy: ['Team Member'], entryPoints: ['Team Member — Mobile Verification'], modes: ['Verify', 'Re-verify'], purpose: 'Verifies a Team Member mobile number.', fieldLabels: ['Mobile Number', 'Verification Code'] },
  { id: 'FORM-000052', formKey: 'customer_password_recovery_form', formName: 'Customer Password Recovery Form', category: 'Passwords & Security', owningModule: 'Customers', usedBy: ['Visitor', 'Customer'], entryPoints: ['Customer Login — Forgot Password'], modes: ['Password Recovery'], purpose: 'Recovers a Customer password.', fieldLabels: ['Email Address'] },
  { id: 'FORM-000053', formKey: 'business_password_recovery_form', formName: 'Business Password Recovery Form', category: 'Passwords & Security', owningModule: 'Businesses', usedBy: ['Visitor', 'Business'], entryPoints: ['Business Login — Forgot Password'], modes: ['Password Recovery'], purpose: 'Recovers a Business password.', fieldLabels: ['Email Address'] },
  { id: 'FORM-000054', formKey: 'customer_change_password_form', formName: 'Customer Change Password Form', category: 'Passwords & Security', owningModule: 'Customers', usedBy: ['Customer'], entryPoints: ['Customer Settings — Change Password'], modes: ['Change Password'], purpose: 'Changes a Customer password.', fieldLabels: ['Current Password', 'New Password', 'Confirm Password'] },
  { id: 'FORM-000055', formKey: 'business_change_password_form', formName: 'Business Change Password Form', category: 'Passwords & Security', owningModule: 'Businesses', usedBy: ['Business'], entryPoints: ['Business Settings — Change Password'], modes: ['Change Password'], purpose: 'Changes a Business password.', fieldLabels: ['Current Password', 'New Password', 'Confirm Password'] },
  { id: 'FORM-000056', formKey: 'mfa_setup_form', formName: 'MFA Setup Form', category: 'Passwords & Security', owningModule: 'Settings', usedBy: ['Platform Owner', 'Co-owner', 'Team Member', 'Business'], entryPoints: ['Security — MFA Setup'], modes: ['Enrol'], purpose: 'Platform input for MFA enrolment.', fieldLabels: ['Authentication Code'] },
  { id: 'FORM-000057', formKey: 'mfa_verification_form', formName: 'MFA Verification Form', category: 'Passwords & Security', owningModule: 'Settings', usedBy: ['Platform Owner', 'Co-owner', 'Team Member', 'Business'], entryPoints: ['Admin Login — MFA', 'Business Security — MFA'], modes: ['Verify'], purpose: 'Platform input for an MFA challenge.', fieldLabels: ['Authentication Code'] },
  { id: 'FORM-000058', formKey: 'subscription_cancellation_form', formName: 'Subscription Cancellation Form', category: 'Plans & Subscriptions', owningModule: 'Billing & Subscriptions', usedBy: ['Business'], entryPoints: ['Business Dashboard — Cancel Subscription'], modes: ['Cancel'], purpose: 'Submits cancellation of the current subscription.', fieldLabels: ['Cancellation Reason', 'Confirmation'] },
  { id: 'FORM-000059', formKey: 'subscription_upgrade_form', formName: 'Subscription Upgrade Form', category: 'Plans & Subscriptions', owningModule: 'Billing & Subscriptions', usedBy: ['Business'], entryPoints: ['Business Dashboard — Upgrade'], modes: ['Upgrade'], purpose: 'Submits an upgrade to the higher plan.', fieldLabels: ['Target Plan', 'Confirmation'] },
  { id: 'FORM-000060', formKey: 'subscription_downgrade_form', formName: 'Subscription Downgrade Form', category: 'Plans & Subscriptions', owningModule: 'Billing & Subscriptions', usedBy: ['Business'], entryPoints: ['Business Dashboard — Downgrade'], modes: ['Downgrade'], purpose: 'Submits a downgrade to the lower plan.', fieldLabels: ['Target Plan', 'Confirmation'] },
  { id: 'FORM-000061', formKey: 'payment_method_update_form', formName: 'Payment Method Update Form', category: 'Plans & Subscriptions', owningModule: 'Billing & Subscriptions', usedBy: ['Business'], entryPoints: ['Business Dashboard — Payment Method'], modes: ['Update'], purpose: 'Platform input for updating the subscription payment method.', fieldLabels: ['Payment Method Token'] },
  { id: 'FORM-000062', formKey: 'access_assignment_form', formName: 'Access Assignment Form', category: 'Team Members', owningModule: 'Team Members', usedBy: ['Platform Owner', 'Co-owner'], entryPoints: ['Admin Dashboard — Access Assignment'], modes: ['Assign'], purpose: 'Assigns access for a Team Member.', fieldLabels: ['Team Member', 'Access Level', 'Modules'] },
];

const catalogueToForm = (entry, fieldStart) => {
  const modes = [...(entry.modes || [])];
  const fields = entry.fieldsBuilder
    ? entry.fieldsBuilder()
    : makeSimpleFields(fieldStart, entry.fieldLabels || ['Input'], modes);
  return createEmptyForm({
    id: entry.id,
    formKey: entry.formKey,
    formName: entry.formName,
    purpose: entry.purpose,
    category: entry.category,
    owningModule: entry.owningModule,
    usedBy: [...(entry.usedBy || [])],
    entryPoints: [...(entry.entryPoints || [])],
    modes,
    dateAdded: SAMPLE_STAMP,
    addedBy: SAMPLE_ACTOR,
    lastUpdated: entry.id === 'FORM-000020' || entry.id === 'FORM-000004' ? SAMPLE_STAMP_UPDATED : SAMPLE_STAMP,
    lastUpdatedBy: SAMPLE_ACTOR,
    fields,
  });
};

export const buildSampleFormInventory = () => {
  let fieldStart = 1;
  return STARTING_FORM_CATALOGUE.map((entry) => {
    const form = catalogueToForm(entry, fieldStart);
    fieldStart += Math.max(form.fields.length, 1);
    return form;
  });
};

/** LBT skeletons remain available to Compliance — excluded from the Form Inventory catalogue. */
export const LBT_FORM_CATEGORY = 'Licences, Badges & Tickets';

export const LBT_FORM_TYPE_MATCHERS = {
  licence: (form) => {
    const key = getFormKey(form).toLowerCase();
    const name = String(form?.formName || '').trim().toLowerCase();
    return key === 'licence_form' || name === 'licence form' || name.startsWith('licence ');
  },
  badge: (form) => {
    const key = getFormKey(form).toLowerCase();
    const name = String(form?.formName || '').trim().toLowerCase();
    return key.startsWith('badge_') || name.startsWith('badge ');
  },
  trade_ticket: (form) => {
    const key = getFormKey(form).toLowerCase();
    const name = String(form?.formName || '').trim().toLowerCase();
    return (
      key === 'trade_ticket_form' ||
      key === 'ticket_form' ||
      name === 'ticket form' ||
      name.startsWith('ticket ')
    );
  },
};

const LBT_SPEC_FORMS = [
  {
    formKey: 'licence_form',
    formName: 'Licence Form',
    purpose: 'Collects Licence application inputs for Compliance.',
    modes: ['Submit', 'Edit', 'Renew', 'Resubmit'],
    fields: [
      { fieldLabel: 'Holder Name', controlType: 'Text input', baseRequirement: 'Required' },
      { fieldLabel: 'Licence Number', controlType: 'Text input', baseRequirement: 'Required' },
      { fieldLabel: 'Issuing Authority', controlType: 'Dropdown', baseRequirement: 'Required' },
      { fieldLabel: 'Jurisdiction', controlType: 'Dropdown', baseRequirement: 'Required' },
      { fieldLabel: 'Licence Class', controlType: 'Multi-select dropdown', baseRequirement: 'Required' },
      { fieldLabel: 'Issue Date', controlType: 'Date picker', baseRequirement: 'Required' },
      { fieldLabel: 'Expiry Date', controlType: 'Date picker', baseRequirement: 'Required' },
      { fieldLabel: 'Evidence', controlType: 'File upload', baseRequirement: 'Required' },
    ],
  },
  {
    formKey: 'badge_first_aid_form',
    formName: 'Badge First Aid Form',
    purpose: 'Collects First Aid badge application inputs.',
    modes: ['Submit', 'Edit', 'Renew', 'Resubmit'],
    fields: [
      { fieldLabel: 'Holder Name', controlType: 'Text input', baseRequirement: 'Required' },
      { fieldLabel: 'Certificate / Reference Number', controlType: 'Text input', baseRequirement: 'Optional' },
      { fieldLabel: 'Issuing Organisation', controlType: 'Text input', baseRequirement: 'Required' },
      { fieldLabel: 'Issue Date', controlType: 'Date picker', baseRequirement: 'Required' },
      { fieldLabel: 'Expiry Date', controlType: 'Date picker', baseRequirement: 'Optional' },
      { fieldLabel: 'Evidence', controlType: 'File upload', baseRequirement: 'Required' },
    ],
  },
  {
    formKey: 'badge_insurance_form',
    formName: 'Badge Insurance Form',
    purpose: 'Collects Insurance badge application inputs.',
    modes: ['Submit', 'Edit', 'Renew', 'Resubmit'],
    fields: [
      { fieldLabel: 'Policyholder / Insured Name', controlType: 'Text input', baseRequirement: 'Required' },
      { fieldLabel: 'Insurer', controlType: 'Text input', baseRequirement: 'Required' },
      { fieldLabel: 'Policy Number', controlType: 'Text input', baseRequirement: 'Required' },
      { fieldLabel: 'Cover Start Date', controlType: 'Date picker', baseRequirement: 'Required' },
      { fieldLabel: 'Cover End Date', controlType: 'Date picker', baseRequirement: 'Required' },
      { fieldLabel: 'Evidence', controlType: 'File upload', baseRequirement: 'Required' },
    ],
  },
  {
    formKey: 'badge_working_with_children_check_form',
    formName: 'Badge Working with Children Check Form',
    purpose: 'Collects WWCC badge application inputs.',
    modes: ['Submit', 'Edit', 'Renew', 'Resubmit'],
    fields: [
      { fieldLabel: 'Holder Name', controlType: 'Text input', baseRequirement: 'Required' },
      { fieldLabel: 'Check / Reference Number', controlType: 'Text input', baseRequirement: 'Optional' },
      { fieldLabel: 'Issuing Organisation', controlType: 'Text input', baseRequirement: 'Required' },
      { fieldLabel: 'Issue Date', controlType: 'Date picker', baseRequirement: 'Required' },
      { fieldLabel: 'Expiry Date', controlType: 'Date picker', baseRequirement: 'Optional' },
      { fieldLabel: 'Evidence', controlType: 'File upload', baseRequirement: 'Required' },
    ],
  },
  {
    formKey: 'trade_ticket_form',
    formName: 'Ticket Form',
    purpose: 'Collects Ticket application inputs for Compliance.',
    modes: ['Submit', 'Edit', 'Renew', 'Resubmit'],
    fields: [
      { fieldLabel: 'Holder Name', controlType: 'Text input', baseRequirement: 'Required' },
      { fieldLabel: 'Ticket / Certificate / Reference Number', controlType: 'Text input', baseRequirement: 'Required' },
      { fieldLabel: 'Issuing Organisation', controlType: 'Text input', baseRequirement: 'Required' },
      { fieldLabel: 'Issue Date', controlType: 'Date picker', baseRequirement: 'Required' },
      { fieldLabel: 'Expiry Date', controlType: 'Date picker', baseRequirement: 'Optional' },
      { fieldLabel: 'Evidence', controlType: 'File upload', baseRequirement: 'Required' },
    ],
  },
];

export const buildLbtFormInventoryForms = () => {
  let fieldNum = 9000;
  let formNum = 900;
  return LBT_SPEC_FORMS.map((spec) => {
    const modes = [...spec.modes];
    const fields = spec.fields.map((field, index) => {
      const created = makeField(fieldNum + index, field.fieldLabel, modes, index + 1, {
        controlType: field.controlType,
        sampleValue: field.baseRequirement === 'Optional' ? 'Optional sample' : 'Sample',
        destination: 'Compliance → Application Input',
      });
      // Keep requirement only for Compliance LBT display (not Form Inventory catalogue).
      created.baseRequirement = field.baseRequirement;
      return created;
    });
    fieldNum += spec.fields.length;
    const form = createEmptyForm({
      id: formatFormId(formNum),
      formKey: spec.formKey,
      formName: spec.formName,
      purpose: spec.purpose,
      category: LBT_FORM_CATEGORY,
      owningModule: 'Compliance',
      usedBy: ['Business'],
      entryPoints: [spec.formName],
      modes,
      dateAdded: SAMPLE_STAMP,
      addedBy: SAMPLE_ACTOR,
      lastUpdated: SAMPLE_STAMP,
      lastUpdatedBy: SAMPLE_ACTOR,
      fields,
    });
    formNum += 1;
    return form;
  });
};

export function isLbtRelatedForm(form) {
  if (!form) return false;
  if (form.category === LBT_FORM_CATEGORY) return true;
  return Object.values(LBT_FORM_TYPE_MATCHERS).some((match) => match(form));
}

export function getLbtFormsForItemType(forms = [], itemType = '') {
  const pool = (forms || []).some(isLbtRelatedForm) ? forms : [...forms, ...buildLbtFormInventoryForms()];
  const matcher = LBT_FORM_TYPE_MATCHERS[itemType];
  if (!matcher) return [];
  return pool.filter((form) => matcher(form));
}

export const matchesFormInventorySearch = (form, query) => {
  const q = String(query || '').trim().toLowerCase();
  if (!q) return true;
  const fieldHits = (form.fields || []).some(
    (field) =>
      String(field.id || '').toLowerCase().includes(q) ||
      String(field.fieldLabel || '').toLowerCase().includes(q) ||
      getFieldKey(field).toLowerCase().includes(q),
  );
  return (
    String(form.id || '').toLowerCase().includes(q) ||
    String(form.formName || '').toLowerCase().includes(q) ||
    getFormKey(form).toLowerCase().includes(q) ||
    fieldHits
  );
};

export const matchesFormInventoryFilters = (form, filters = {}) => {
  const { category = 'all', owningModule = 'all', usedBy = 'all' } = filters;
  if (category !== 'all' && form.category !== category) return false;
  if (owningModule !== 'all' && form.owningModule !== owningModule) return false;
  if (usedBy !== 'all' && !(form.usedBy || []).includes(usedBy)) return false;
  return true;
};

export const getCategoryFilterOptions = () => [
  { value: 'all', label: 'All' },
  ...FORM_CATEGORY_OPTIONS.map((v) => ({ value: v, label: v })),
];

export const getOwningModuleFilterOptions = () => [
  { value: 'all', label: 'All' },
  ...OWNING_MODULE_OPTIONS.map((v) => ({ value: v, label: v })),
];

export const getUsedByFilterOptions = () => [
  { value: 'all', label: 'All' },
  ...USED_BY_OPTIONS.map((v) => ({ value: v, label: v })),
];

export const nextFormIdNumber = (forms = []) => {
  let max = 0;
  forms.forEach((form) => {
    const match = String(form.id || '').match(/^FORM-(\d+)$/i);
    if (match) max = Math.max(max, Number(match[1]));
  });
  // Skip historically preserved unused numbers when generating the next ID.
  const reserved = new Set([7, 8, 9, 27, 36, 37, 38]);
  let next = max + 1;
  while (reserved.has(next)) next += 1;
  return next;
};

export const nextFieldIdNumber = (forms = []) => {
  let max = 0;
  forms.forEach((form) => {
    (form.fields || []).forEach((field) => {
      const match = String(field.id || '').match(/^(?:FORM-)?FIELD-(\d+)$/i);
      if (match) max = Math.max(max, Number(match[1]));
    });
  });
  return max + 1;
};

export const findFormByIdOrSlug = (forms, param) => {
  const decoded = decodeURIComponent(param || '');
  return forms.find(
    (form) =>
      form.id === decoded ||
      String(form.id || '').toLowerCase() === decoded.toLowerCase() ||
      getFormKey(form) === decoded ||
      String(form.formName || '').toLowerCase() === decoded.toLowerCase(),
  );
};
